import React from "react";
import { connect } from "react-redux";
import ReactTooltip from "react-tooltip";

import MainMenu from "../../components/menus/MainMenu";
import CommonToolbar from "../../components/toolbar/CommonToolbar";
import AppsMenu from "../../components/appsmenu/AppsMenu";
import { IAppState } from "../../state/Store";
import { AuthJWTPayload } from "../../types/system/AuthJWT";
import UserMenu from "../usermenu/UserMenu";
import Configuration from "../../foundation/Configuration";
import NotificationsMenu from "../notifications/NotificationsMenu";
import AppState from "../../foundation/AppState";
import SettingsMenu from "../settings/SettingsMenu";
import {
    setSidebarOpen,
    setSidebarWidth,
} from "../../state/system/UIConfiguration";
import ErrorBoundary from "../error/ErrorBoundary";
import User from "../../types/core/User";

import userIcon from "../../graphics/profile.svg";

import "./Sidebar.scss";

interface SidebarProps {
    user: User;
    profilePicture: string;
    sidebarWidth: number;
    sidebarOpen: boolean;

    viewFilters?: any[];

    impersonated: boolean;
    jwt: AuthJWTPayload;
    filename: string;
    sidebarPinned: boolean;
    currentDate: Date;
    currentDateCode: string;
    currentDateStatus: number;

    setSidebarOpen: Function;
    setSidebarWidth: Function;
    displayClicked?: Function;
    shareClicked?: Function;
}

interface SidebarState {
    dragging: boolean;
    appsOpen: boolean;
    notificationsOpen: boolean;
    settingsOpen: boolean;
    userMenuOpen: boolean;
    fileBased: boolean;
    fileBasedSingle: boolean;
    dateBased: boolean;
}

class Sidebar extends React.Component<SidebarProps, SidebarState> {
    state = {
        dragging: false,
        appsOpen: false,
        notificationsOpen: false,
        settingsOpen: false,
        userMenuOpen: false,
        fileBased: Configuration.fileBased,
        fileBasedSingle: Configuration.fileBasedSingle,
        dateBased: Configuration.dateBased,
    };

    // constructor(props: SidebarProps) {
    //     super(props);
    // }

    componentDidUpdate(
        prevProps: Readonly<SidebarProps>,
        prevState: Readonly<SidebarState>,
        snapshot?: any,
    ): void {
        if (this.props.sidebarOpen !== prevProps.sidebarOpen)
            ReactTooltip.rebuild();
    }

    gripDown = (e: React.MouseEvent) => {
        if (!this.props.sidebarOpen) return;

        e.preventDefault();
        const startX = e.clientX;
        const startWidth = this.props.sidebarWidth;

        this.setState({ dragging: true });

        const onMouseMove = (e: MouseEvent) => {
            const newWidth = Math.min(
                600,
                Math.max(300, startWidth + (startX - e.clientX)),
            );
            this.props.setSidebarWidth(newWidth);
        };

        const onMouseUp = () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            this.setState({ dragging: false });
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
    };

    toggleSidebar = () => {
        this.props.setSidebarOpen(!this.props.sidebarOpen);
    };

    toggleUserMenu = () => {
        const willOpen = !this.state.userMenuOpen;
        this.setState(prevState => ({
            userMenuOpen: willOpen,
            appsOpen: willOpen ? false : prevState.appsOpen,
            notificationsOpen: willOpen ? false : prevState.notificationsOpen,
            settingsOpen: willOpen ? false : prevState.settingsOpen,
        }));
    };

    toggleApps = () => {
        const willOpen = !this.state.appsOpen;
        this.setState(prevState => ({
            userMenuOpen: willOpen ? false : prevState.userMenuOpen,
            appsOpen: willOpen,
            notificationsOpen: willOpen ? false : prevState.notificationsOpen,
            settingsOpen: willOpen ? false : prevState.settingsOpen,
        }));
    };

    toggleNotifications = () => {
        const willOpen = !this.state.notificationsOpen;
        this.setState(prevState => ({
            userMenuOpen: willOpen ? false : prevState.userMenuOpen,
            appsOpen: willOpen ? false : prevState.appsOpen,
            notificationsOpen: willOpen,
            settingsOpen: willOpen ? false : prevState.settingsOpen,
        }));
    };

    toggleSettings = () => {
        const willOpen = !this.state.settingsOpen;
        this.setState(prevState => ({
            userMenuOpen: willOpen ? false : prevState.userMenuOpen,
            appsOpen: willOpen ? false : prevState.appsOpen,
            notificationsOpen: willOpen ? false : prevState.notificationsOpen,
            settingsOpen: willOpen,
        }));
    };

    userMenuClosed = () => {
        this.setState(() => ({ userMenuOpen: false }));
    };

    render() {
        return (
            <div
                className={`sidebar no-print ${
                    this.props.sidebarOpen ? "open" : ""
                }${this.state.dragging ? " dragging" : ""}`}
                style={{
                    flexBasis: this.props.sidebarWidth,
                }}>
                <div className="sidebar-slide">
                    <div className="sidebar-header">
                        <span
                            className={`user ${
                                this.state.userMenuOpen ? "open" : ""
                            } ${this.props.impersonated ? "impersonated" : ""}`}
                            onClick={this.toggleUserMenu}
                            tabIndex={0}>
                            <div className="profile-image">
                                <div className="default">
                                    <img
                                        src={this.props?.profilePicture?.toDataUrl() ?? userIcon}
                                        alt="Profile"
                                        onError={e => {
                                            e.currentTarget.src = userIcon;
                                        }}
                                    />
                                </div>
                            </div>
                            <span className="user-details">
                                <span className="username-display">
                                    {this.props.jwt?.given_name}{" "}
                                    {this.props.jwt?.family_name}
                                </span>
                                <span className="school-display">
                                    {this.props.jwt?.school}
                                </span>
                            </span>
                        </span>
                    </div>

                    {this.state.userMenuOpen ? (
                        <UserMenu shouldClose={this.userMenuClosed}></UserMenu>
                    ) : (
                        <></>
                    )}

                    <div
                        className={`controls ${
                            this.props.viewFilters &&
                            this.props.viewFilters.length > 0
                                ? "has-filters"
                                : ""
                        }`}>
                        <ErrorBoundary sectionName="Main Toolbar">
                            <CommonToolbar
                                appsOpen={this.state.appsOpen}
                                notificationsOpen={this.state.notificationsOpen}
                                settingsOpen={this.state.settingsOpen}
                                displayClicked={this.props.displayClicked}
                                shareClicked={this.props.shareClicked}
                                appsClicked={this.toggleApps}
                                notificationsClicked={this.toggleNotifications}
                                settingsClicked={this.toggleSettings}
                            />
                        </ErrorBoundary>

                        <div className="view-filters">
                            {/* <a className="filter" href="#" ng-repeat="item in viewFilters">
                                <span className="filter-type">{{item.type}}:</span>
                                <span className="filter-name">{{item.name}}</span>
                                <span className="filter-remove" ng-click="actions.removeFilter(item)"></span>
                            </a> */}
                        </div>

                        <div className="sidebar-container">
                            <div className="tree-scroll">
                                <ErrorBoundary sectionName="Main Menu">
                                    <MainMenu
                                        collapsed={!this.props.sidebarOpen}
                                    />
                                </ErrorBoundary>
                            </div>

                            <ErrorBoundary sectionName="Apps Menu">
                                <AppsMenu open={this.state.appsOpen} />
                            </ErrorBoundary>

                            <ErrorBoundary sectionName="Notifications Menu">
                                <NotificationsMenu
                                    open={this.state.notificationsOpen}
                                    notifications={[]}
                                    users={[]}
                                />
                            </ErrorBoundary>

                            <ErrorBoundary sectionName="Settings Menu">
                                <SettingsMenu open={this.state.settingsOpen} />
                            </ErrorBoundary>
                        </div>

                        <ErrorBoundary sectionName="File & Date Information">
                            <div
                                className={`status ${
                                    this.state.dateBased ? "has-date" : ""
                                }`}>
                                {this.state.dateBased ? (
                                    <div className="date-container">
                                        <button
                                            className="back"
                                            ng-disabled="processingDate"
                                            ng-click="actions.prevDate()"></button>
                                        <button
                                            className="display"
                                            ng-disabled="processingDate"
                                            ng-click="actions.showDates()"
                                            data-place="left"
                                            data-tip={
                                                this.props.currentDateStatus
                                                    ? this.props
                                                          .currentDateStatus < 0
                                                        ? "Completed"
                                                        : this.props
                                                              .currentDateStatus >
                                                          0
                                                        ? "Future"
                                                        : "Today"
                                                    : "Today"
                                            }>
                                            <span className="normal">
                                                {String(this.props.currentDate)}
                                            </span>
                                            <span className="normal">
                                                {this.props.currentDateCode}
                                            </span>{" "}
                                            <span className="normal">
                                                (
                                                <span className="normal">
                                                    {this.props
                                                        .currentDateStatus
                                                        ? this.props
                                                              .currentDateStatus <
                                                          0
                                                            ? "Completed"
                                                            : this.props
                                                                  .currentDateStatus >
                                                              0
                                                            ? "Future"
                                                            : "Today"
                                                        : "Today"}
                                                </span>
                                                )
                                            </span>
                                        </button>
                                        <button
                                            className="forward"
                                            ng-disabled="processingDate"
                                            ng-click="actions.nextDate()"></button>
                                    </div>
                                ) : (
                                    <></>
                                )}
                                <div className="file-container">
                                    <div
                                        className="expand-button"
                                        tabIndex={0}
                                        onClick={this.toggleSidebar}
                                        data-place="left"
                                        data-tip={
                                            this.props.sidebarOpen
                                                ? "Close sidebar"
                                                : "Open sidebar"
                                        }>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                    </div>

                                    {this.state.fileBased ? (
                                        this.state.fileBasedSingle ? (
                                            <span ng-if="fileBased&&singleFileBased">
                                                {this.props.filename}
                                            </span>
                                        ) : (
                                            <select
                                                className="file-list"
                                                title="Select file"
                                                // select-editor-binding="files"
                                                // custom-mappings="{text:'Name',value:'Id'}"
                                                // ng-model="model.selectedFileId"
                                                // ng-change="actions.fileSelectChange()"
                                            ></select>
                                        )
                                    ) : (
                                        <span ng-if="!fileBased">
                                            {this.props.jwt?.school}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </ErrorBoundary>
                    </div>
                </div>

                <div
                    className="sidebar-grip"
                    onMouseDown={this.gripDown}
                    ng-show="sidebarOpen"></div>
            </div>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { ui } = state.db;
    const { session } = state.session;
    return {
        user: session.user,
        profilePicture: session.profilePicture,
        sidebarWidth: ui.sidebarWidth,
        sidebarOpen: ui.sidebarOpen,

        impersonated: AppState.jwt?.impersonating !== Number(AppState.jwt?.sub),
        jwt: AppState.jwt,
        filename: "",
        sidebarPinned: false,
        currentDate: new Date(),
        currentDateCode: "",
        currentDateStatus: 0,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setSidebarOpen: (value: boolean) => dispatch(setSidebarOpen(value)),
        setSidebarWidth: (value: number) => dispatch(setSidebarWidth(value)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
